/**
 * Evaluation Forms Component
 * 
 * Allows admins to view and download submitted evaluation forms as PDFs.
 * Part of the Admin Panel (admin-level access only).
 */

import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { FileText, Download, Calendar, Building2, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function EvaluationForms() {
  const { isDark } = useTheme();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/software-evaluation');
      setForms(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Failed to load evaluation forms:', err);
      setError(err.data?.message || err.message || 'Failed to load evaluation forms.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return value;
  };

  const downloadPDF = async (form) => {
    setDownloadingId(form.id);
    try {
      // Fetch full form data
      const fullForm = await api.get(`/api/software-evaluation/${form.id}`);
      
      // Create a temporary div to render the PDF content
      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute';
      pdfContainer.style.left = '-9999px';
      pdfContainer.style.width = '210mm'; // A4 width
      pdfContainer.style.padding = '20mm';
      pdfContainer.style.backgroundColor = '#ffffff';
      pdfContainer.style.fontFamily = 'Arial, sans-serif';
      pdfContainer.style.color = '#272c3a';
      pdfContainer.style.fontSize = '11px';
      pdfContainer.style.lineHeight = '1.6';

      // Build the PDF HTML content with submitted form styling
      pdfContainer.innerHTML = `
        <div style="max-width: 170mm; margin: 0 auto;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #3374ba;">
            <h1 style="color: #272c3a; font-size: 28px; font-weight: bold; margin: 10px 0;">Aegis Scope Evaluation</h1>
            <div style="background: #3374ba; color: white; padding: 8px 16px; display: inline-block; border-radius: 6px; font-size: 12px; font-weight: bold; margin-top: 10px; letter-spacing: 1px;">
              ✓ SUBMITTED FORM
            </div>
          </div>

          <!-- Submission Info -->
          <div style="background: #f0f4f8; padding: 15px; border-radius: 8px; margin-bottom: 25px; border-left: 4px solid #3374ba;">
            <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
              <div><strong>Submitted:</strong> ${formatDate(fullForm.submitted_at)}</div>
              <div><strong>Form ID:</strong> ${fullForm.id.substring(0, 8)}...</div>
            </div>
          </div>

          <!-- Company Information -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Company Insights</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Tell us about your organization</p>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Company Name</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.company_name)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Primary Contact</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.company_contact)}</div>
              </div>
            </div>
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Work Email</div>
              <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.email)}</div>
            </div>
          </div>

          <!-- Pain Points & Current State -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Operational Pain Points</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Identify current bottlenecks</p>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Top 3 Pain Points</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.top_pain_points)}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Search Time Percentage (%)</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.time_searching_percentage)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Frequency of Missing Assets</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.asset_missing_frequency)}</div>
              </div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Current Tracking System</div>
              <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.current_tracking_system)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Limitations of Current Solution</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.current_system_limitations)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Missing Features/Capabilities</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.missing_features)}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Annual Replacement Cost ($)</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.annual_replacement_cost)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Unauthorized Theft</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.unauthorized_theft)}</div>
              </div>
            </div>
          </div>

          <!-- Asset & Equipment Information -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Asset Specifications</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">What are we tracking?</p>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Asset Types</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.asset_types_to_track)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Other Equipment Types</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.other_equipment_types)}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Total Asset Count</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.total_assets_count)}</div>
              </div>
            </div>
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">High-Value/Critical Assets</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.high_value_assets)}</div>
            </div>
          </div>

          <!-- Facility Information -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Facility & IT Landscape</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Infrastructure requirements</p>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Facility Square Footage</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.facility_square_footage)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Active Forklifts Count</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.forklift_count)}</div>
              </div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Device Installation Locations</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.device_installation_locations)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Prohibited Zones</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.prohibited_zones)}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Facility-Wide WiFi</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.has_wifi)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">3-Phase Electrical</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.has_three_phase_electrical)}</div>
              </div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Existing IT Infrastructure</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.existing_it_infrastructure)}</div>
            </div>
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Cyber Security Protocols</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.cyber_security_protocol)}</div>
            </div>
          </div>

          <!-- Tracking Requirements -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Tracking Requirements</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Precision and visibility</p>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Estimated PLX Tags Needed</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.plx_tags_count)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">GPS Tracking Units</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.gps_tracking_count)}</div>
              </div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Inter-Facility Movement</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.inter_facility_movement)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Number of Facilities</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.facilities_count)}</div>
              </div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Desired Accuracy Metric</div>
              <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.accuracy_metric)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Visibility Type</div>
              <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.visibility_type)}</div>
            </div>
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Supplier Visibility Needs</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.supplier_visibility_needs)}</div>
            </div>
          </div>

          <!-- User Access & Integration -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Integration & Users</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Access and connectivity</p>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Target User Count</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.user_count)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Different Permission Levels</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.different_permission_levels)}</div>
              </div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Anticipated User Roles</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.user_roles)}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Production Sequencing Needed</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.needs_production_sequencing)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Integration Strategy</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.integration_type)}</div>
              </div>
            </div>
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Existing Production System</div>
              <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.current_production_system)}</div>
            </div>
          </div>

          <!-- Implementation Timeline -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Implementation & Timeline</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Deployment strategy</p>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Ideal Deployment Timeline</div>
              <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.ideal_timeline)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Seasonal Considerations</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.seasonal_considerations)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Implementation Scope</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.implementation_scope)}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Start with a Pilot</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.prefer_pilot)}</div>
              </div>
              ${fullForm.prefer_pilot ? `
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Target Pilot Area</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.pilot_area)}</div>
              </div>
              ` : ''}
            </div>
            ${fullForm.prefer_pilot ? `
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Pilot Success Metrics</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.pilot_success_metrics)}</div>
            </div>
            ` : ''}
          </div>

          <!-- Requirements & Budget -->
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Requirements & Budget</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Critical needs and investment</p>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Critical Features</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.critical_features)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Deal-Breakers</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.deal_breakers)}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Estimated Budget Range</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.budget_range)}</div>
              </div>
              <div>
                <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Preferred Pricing Model</div>
                <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.pricing_model)}</div>
              </div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Key Decision Makers</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.decision_makers)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">ROI Measurement</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.roi_measurement)}</div>
            </div>
            <div style="margin-bottom: 15px;">
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Success at 6 Months</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.success_6_months)}</div>
            </div>
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Success at 12 Months</div>
              <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 3px solid #3374ba; min-height: 40px;">${formatValue(fullForm.success_12_months)}</div>
            </div>
          </div>

          <!-- Additional Information -->
          ${fullForm.referrer_name ? `
          <div style="background: #ffffff; border: 2px solid #e6e7e8; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
            <h2 style="color: #272c3a; font-size: 18px; font-weight: bold; margin-bottom: 5px; border-bottom: 2px solid #3374ba; padding-bottom: 8px;">Additional Information</h2>
            <p style="color: #f7923f; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 15px;">Optional details</p>
            <div>
              <div style="font-weight: bold; color: #272c3a; margin-bottom: 5px; font-size: 10px;">Referrer Name</div>
              <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; border-left: 3px solid #3374ba;">${formatValue(fullForm.referrer_name)}</div>
            </div>
          </div>
          ` : ''}

          <!-- Footer -->
          <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e6e7e8; color: #6b7280; font-size: 10px;">
            <p>This document was generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p style="margin-top: 5px;">Aegis Asset Tracking System - Evaluation Form Submission</p>
          </div>
        </div>
      `;

      document.body.appendChild(pdfContainer);

      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // Convert to canvas and then to PDF
      const canvas = await html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      const fileName = `Evaluation_Form_${fullForm.company_name.replace(/[^a-z0-9]/gi, '_')}_${new Date(fullForm.submitted_at).toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      // Clean up
      document.body.removeChild(pdfContainer);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert('Failed to generate PDF: ' + (err.message || 'Unknown error'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`p-6 border-b flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Evaluation Forms</h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>View and download submitted evaluation forms</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mx-6 mt-4 p-3 rounded-md border flex items-start gap-2 ${
          isDark ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className={`p-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 border-2 border-t-blue-500 rounded-full animate-spin mx-auto mb-4 ${isDark ? 'border-gray-600' : 'border-gray-200'}`}></div>
          Loading evaluation forms...
        </div>
      ) : (
        <div className="overflow-x-auto">
          {forms.length === 0 ? (
            <div className={`p-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No evaluation forms submitted yet.</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className={isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                <tr>
                  <th className="px-6 py-3 font-medium">Company</th>
                  <th className="px-6 py-3 font-medium">Contact</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Assets</th>
                  <th className="px-6 py-3 font-medium">Budget</th>
                  <th className="px-6 py-3 font-medium">Submitted</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                {forms.map((form) => (
                  <tr key={form.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50'}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building2 className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{form.company_name}</span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {form.company_contact || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Mail className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{form.email}</span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {form.total_assets_count || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {form.budget_range || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <div className="flex items-center gap-2">
                        <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span>{formatDate(form.submitted_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => downloadPDF(form)}
                        disabled={downloadingId === form.id}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                          isDark
                            ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 disabled:opacity-50'
                            : 'bg-blue-50 hover:bg-blue-100 text-blue-600 disabled:opacity-50'
                        }`}
                      >
                        <Download className="w-3 h-3" />
                        {downloadingId === form.id ? 'Generating...' : 'Download PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
